import { Fragment, useState } from 'react';
import { useIsFetching, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import * as amplitude from '@amplitude/analytics-browser';

import {
  Button,
  PageStatus,
  SkeletonLoader,
} from '@unified-checkout/design-system';
import {
  Check,
  CircleExclamation,
} from '@unified-checkout/design-system/icons/index';

import {
  REACH_PAGE_EVENTS,
  REACH_PAGE_EVENTS_PROPERTIES,
} from '@app/dashboard/constants/tracking_events/reach';
import { useLangStore } from '@app/dashboard/store/lang';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';

import type {
  IPlan,
  ISmsSubscribedPlanHistory,
} from '@app/dashboard/types/innerLayout/reach/bundles';
import { fetchPlans } from '@app/dashboard/services/inner_layout/reach/bundles';

import SubscriptionModal from './SubscriptionModal';

import styles from './Plans.module.scss';

const Plans = ({ currentPlan }: { currentPlan: ISmsSubscribedPlanHistory }) => {
  const { t } = useTranslation();
  const { lang } = useLangStore();
  const isFetching = useIsFetching();
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    plan: null as IPlan | null,
  });
  const [isCreateSubscriptionLoading, setIsCreateSubscriptionLoading] =
    useState(false);

  const { data: plans = [], isError } = useQuery({
    queryKey: [QUERY_KEYS.REACH_GET_BUNDLES],
    queryFn: fetchPlans,
    select: (data) => data.data?.data?.results || [],
  });

  const handleOpenModal = (plan: IPlan) => {
    setModalConfig({
      isOpen: true,
      plan,
    });
  };

  const handleSubscribeBtnClick = (plan: IPlan) => {
    amplitude.track(REACH_PAGE_EVENTS.BUNDLES_PAGE.SUBSCRIBE_BUNDLE_SELECTED, {
      [REACH_PAGE_EVENTS_PROPERTIES.BUNDLES_PAGE.BUNDLE_PLAN]:
        plan?.name_en || '',
    });

    handleOpenModal(plan);
  };

  return isError ? (
    <PageStatus
      status="error"
      title={t('innerLayout.errors.smthWentWrong')}
      description={t('innerLayout.errors.dataRetrievalErr')}
      icon={CircleExclamation}
    />
  ) : isFetching ? (
    <div className={styles['bundles__plans']}>
      {Array.from({ length: 9 }).map((_, index) => (
        <div key={index} className={styles['bundles__plans-box']}>
          <SkeletonLoader key={index} height="320px" noOfRows={5} />
        </div>
      ))}
    </div>
  ) : (
    <>
      <SubscriptionModal
        modalConfig={modalConfig}
        setModalConfig={setModalConfig}
        onLoadingChange={setIsCreateSubscriptionLoading}
      />
      <div className={styles['bundles__plans']}>
        {plans.map((plan: IPlan) => {
          const planName = lang === 'en' ? plan?.name_en : plan?.name_ar;
          const benefitText = plan.benefits.find((benefit) => benefit)?.[lang];
          const isSubscribed = plan.id === currentPlan.plan?.id;
          const isFreePlanExpired = plan.free_plan && currentPlan.expired;
          const isFreePlanSubscribed = plan.free_plan && plan.subscribed;
          const isFreePlanNotSubscribed = plan.free_plan && !plan.subscribed;

          if (isFreePlanExpired) return null;

          return (
            <Fragment key={plan.id}>
              <div
                className={styles['bundles__plans-box']}
                data-bundle-subscribed={isSubscribed}
              >
                <header
                  className={styles['bundles__plans-box-header']}
                  title={planName}
                >
                  <h3 className={styles['bundles__plans-box-header-title']}>
                    {planName}
                  </h3>
                </header>

                <section className={styles['bundles__plans-box-details']}>
                  <div className={styles['bundles__plans-box-details-wrapper']}>
                    <p className={styles['bundles__plans-box-details-price']}>
                      {t('innerLayout.reach.bundles.price', {
                        price: plan.price,
                      })}
                    </p>
                    <p
                      className={styles['bundles__plans-box-details-duration']}
                    >
                      {t('innerLayout.reach.bundles.targetPlan', {
                        sms: parseInt(plan.amount),
                        days: plan.expire_in,
                      })}
                    </p>

                    {isSubscribed || isFreePlanSubscribed ? (
                      <p className={styles['bundles__plans-box-details-text']}>
                        {t(
                          `innerLayout.reach.bundles.${
                            isSubscribed ? 'subscribed' : 'usedTrial'
                          }`
                        )}
                      </p>
                    ) : (
                      <Button
                        text={t(
                          `innerLayout.reach.bundles.${
                            isFreePlanNotSubscribed ? 'tryNow' : 'subscribe'
                          }`
                        )}
                        onClickHandler={() => handleSubscribeBtnClick(plan)}
                        loading={
                          isCreateSubscriptionLoading &&
                          modalConfig.plan?.id === plan.id
                        }
                        disabled={
                          isCreateSubscriptionLoading &&
                          modalConfig.plan?.id === plan.id
                        }
                      />
                    )}
                  </div>
                </section>

                <footer className={styles['bundles__plans-box-footer']}>
                  <div className={styles['bundles__plans-box-footer-wrapper']}>
                    {!!benefitText && (
                      <div className={styles['bundles__plans-box-footer-icon']}>
                        {Check}
                      </div>
                    )}
                    <p className={styles['bundles__plans-box-footer-text']}>
                      {benefitText}
                    </p>
                  </div>
                </footer>
              </div>
            </Fragment>
          );
        })}
      </div>
    </>
  );
};

export default Plans;
